/*
 * Tracking Event Admin - Subscriptions Management - Add State Event Subscriptions Dialog
 *
 *
 *
 */

import angular from "angular";

import Assert from "tbAssert";
import moment from "moment-timezone";

import ngSanitize from "angular-sanitize";
import ngAria from "angular-aria";
import ngAnimate from "angular-animate";
import uiBootstrap from "angular-ui-bootstrap";
import kendoDirectivesModule from "../../shims/kendoDirectives";

import MessageQueue from "../../models/MessageQueue";
import SortAsComparer from "tbClient/utility/SortAsComparer";
import SubscriptionEditorError from "tbClient/views/trackerbase-admin/SubscriptionEditorError";

import StateOfficeElectionMenuComponentModule from "../../directives/StateOfficeElectionMenu";
import OfficeDataModule from "../../services/OfficeDataService";

const mModalInstance = new WeakMap();
const mOfficeDataSvc = new WeakMap();
const mLogSvc = new WeakMap();
const mQSvc = new WeakMap();

const errorMessageQueueType = "danger";
const mErrorMessageQueue = new WeakMap();

const mSelectedItem = new WeakMap();
const mSubscriptionSelections = new WeakMap();
const mSubscriptionDisplay = new WeakMap();

class StateOfficeElectionSubscriptionsEditorDialogController {
    constructor(
        $uibModalInstance,
        $log,
        $q,
        OfficeData
    ) {
        Assert.StrictAssert($uibModalInstance != null, "Assertion Failure: Missing Modal Instance Dependency");
        Assert.StrictAssert(OfficeData != null, "Assertion Failure: Missing Office Data Service Dependency");

        mModalInstance.set(this, $uibModalInstance);
        mLogSvc.set(this, $log);
        mQSvc.set(this, $q);
        mOfficeDataSvc.set(this, OfficeData);
        mErrorMessageQueue.set(this, new MessageQueue(errorMessageQueueType));

        mSelectedItem.set(this, null);
        mSubscriptionSelections.set(this, {
            StateId: new Set(),
            Office: new Set(),
            Election: new Set()
        });

        mSubscriptionDisplay.set(this, {
            StateId: [],
            Office: [],
            Election: []
        });
    }

    get selectedItem() {
        return mSelectedItem.get(this);
    }

    set selectedItem(updatedVal) {
        mSelectedItem.set(this, updatedVal);
    }

    get errorMessages() {
        return mErrorMessageQueue.get(this).messages;
    }

    closeErrorMessage(msgIdx) {
        mErrorMessageQueue.get(this).removeMessageAtIdx(msgIdx);
    }

    get subscriptionSelections() {
        return mSubscriptionSelections.get(this);
    }

    get subscriptionDisplay() {
        return mSubscriptionDisplay.get(this);
    }

    readElectionsForOffice(officeId) {
        const logSvc = mLogSvc.get(this);
        const qSvc = mQSvc.get(this);
        return mOfficeDataSvc.get(this)
            .get({officeId: officeId})
            .$promise
            .then(
                (officeDataResult) => {
                    logSvc.debug("Office:Election Data Source Read Success", officeDataResult.result);
                    return officeDataResult.result.officeElections;
                },
                (errorResponse) => {
                    logSvc.error("Office:Election Data Source Read Failure:", errorResponse);
                    return qSvc.reject(errorResponse);
                }
            );
    }

    updateSubscriptionDisplay() {
        let items = Object.keys(this.subscriptionSelections);

        let numItems = items.length;
        for (let itemIdx = 0; itemIdx < numItems; ++itemIdx) {
            let item = items[itemIdx];
            this.subscriptionDisplay[item].length = 0;
            Array.prototype.push.apply(
                this.subscriptionDisplay[item],
                Array.from(this.subscriptionSelections[item].values())
            );
        }
    }

    updateCheckedItems(updatedItem, updatedItemType) {
        let targetCollection = null;
        let itemId = null;

        switch (updatedItemType) {
            case "State":
                targetCollection = this.subscriptionSelections.StateId;
                itemId = updatedItem.stateId;
                break;
            case "Office":
                targetCollection = this.subscriptionSelections.Office;
                itemId = updatedItem.officeId;
                break;
            case "Election":
                targetCollection = this.subscriptionSelections.Election;
                itemId = updatedItem.electionId;
                break;
            default:
                return;
        }
        if (updatedItem.checked === true) {
            targetCollection.add(itemId);
        }
        else if (updatedItem.checked === false) {
            targetCollection.delete(itemId);
        }
    }

    okAndConvertSelectedOfficesToNextElection() {
        if (!this.canAddSubsWithOfficeElectionConversion()) {
            return false;
        }

        const logSvc = mLogSvc.get(this);
        const qSvc = mQSvc.get(this);
        const presentTime = moment.utc();

        const officeSelections = Array.from(this.subscriptionSelections.Office.values());

        const readAllOfficeElectionsPromise = qSvc.all(officeSelections.map((officeId) => {
            return this.readElectionsForOffice(officeId).then(
                (officeElections) => {
                    officeElections.sort(SortAsComparer);
                    const nextElection = officeElections.find((val) => moment(val.electionDate).isAfter(presentTime, "day"));
                    if (nextElection !== undefined) {
                        logSvc.debug(`Read office ${officeId}, would add the following election to current selections`, nextElection);
                        return {office: officeId, nextElection: nextElection.electionId};
                    }
                    else {
                        // No next election found.
                        logSvc.debug(`No future elections found for office ${officeId}`);
                        return {office: officeId, nextElection: null};
                    }
                },
                (errorResult) => {
                    logSvc.error(`Retrieving election data for office ${officeId} failed`, errorResult);
                    return qSvc.reject({failedOffice: officeId, reason: errorResult});
                }
            );
        }));

        readAllOfficeElectionsPromise.then(
            (officeElectionResults) => {
                const resultMessages = [];
                officeElectionResults.forEach(({office: officeId, nextElection: nextElectionId}) => {
                    if (nextElectionId != null) {
                        this.subscriptionSelections.Office.delete(officeId);
                        this.subscriptionSelections.Election.add(nextElectionId);
                    }
                    else {
                        resultMessages.push({
                            subTargetType: "Office",
                            subTargetId: officeId,
                            msgs: [0x3000, 0x3001]
                        });
                    }
                });
                this.ok(resultMessages);
            },
            (errorResponses) => {
                mModalInstance.get(this).dismiss(new SubscriptionEditorError({
                    isError: true,
                    errorMessage: "Failed to read election data for at least one office",
                    errorData: errorResponses
                }));
            }
        );
    }

    changeSelectedItem(selectedMenuItem, selectedMenuItemType) {
        this.selectedItem = selectedMenuItem;
        angular.noop(selectedMenuItemType);
    }

    canAddStateOfficeElectionSubs() {
        const numStateSubsToAdd = this.subscriptionSelections.StateId.size;
        const numOfficeSubsToAdd = this.subscriptionSelections.Office.size;
        const numElectionSubsToAdd = this.subscriptionSelections.Election.size;
        const totalSubsToAdd = numStateSubsToAdd + numOfficeSubsToAdd + numElectionSubsToAdd;

        return (totalSubsToAdd > 0);
    }

    canAddSubsWithOfficeElectionConversion() {
        const numOfficeSubsToAdd = this.subscriptionSelections.Office.size;
        return (numOfficeSubsToAdd > 0);
    }

    cancel() {
        mModalInstance.get(this).dismiss(SubscriptionEditorError.MakeEditorCancelledResult());
    }

    ok(resultMessages) {
        const subscriptionSelections = this.subscriptionSelections;
        const editorMsgs = [];
        if (resultMessages != null) {
            editorMsgs.push(...resultMessages);
        }

        mModalInstance.get(this).close({
            subs: {
                StateId: Array.from(subscriptionSelections.StateId.values()),
                Office: Array.from(subscriptionSelections.Office.values()),
                Election: Array.from(subscriptionSelections.Election.values())
            },
            msgs: editorMsgs
        });
    }
}

StateOfficeElectionSubscriptionsEditorDialogController.$inject = [
    "$uibModalInstance",
    "$log",
    "$q",
    "OfficeData",
];

StateOfficeElectionSubscriptionsEditorDialogController.dependencies = [
    ngAnimate,
    ngAria,
    ngSanitize,
    uiBootstrap,
    kendoDirectivesModule,
    StateOfficeElectionMenuComponentModule,
    OfficeDataModule
];

StateOfficeElectionSubscriptionsEditorDialogController.template = require("./StateOfficeElectionSubscriptionsEditorDialog.tpl.html");
StateOfficeElectionSubscriptionsEditorDialogController.controllerAs = "tbStateOfficeElectionSubMgr";

export default StateOfficeElectionSubscriptionsEditorDialogController;



//////////////////
// WEBPACK FOOTER
// ./src/views/trackerbase-admin/StateOfficeElectionSubscriptionsEditorDialog.js
// module id = EEhM
// module chunks = 1